import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderSlot as _renderSlot, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = ["role", "aria-live", "aria-label", "aria-labelledby"]
const _hoisted_2 = { class: "notification__wrapper" }
const _hoisted_3 = { class: "notification__body" }
const _hoisted_4 = ["id"]
const _hoisted_5 = { class: "notification__message" }

import { computed, ref, watch } from 'vue';
import useTranslations from '@/composables/useTranslations';


export default /*@__PURE__*/_defineComponent({
  __name: 'Notification',
  props: {
    title: { default: undefined },
    themeColor: { default: 'primary' },
    fillMode: { default: 'light' },
    iconClass: { default: undefined },
    withIcon: { type: Boolean, default: false },
    type: { default: 'default' }
  },
  setup(__props: any) {

const props = __props;

const { translate: t } = useTranslations();
const dynamicClass = computed(() =>
  props.fillMode === 'light'
    ? `notification--${props.themeColor}-light`
    : `notification--${props.themeColor}`,
);

const notificationIconClass = computed(() => {
  let iconClass;
  switch (props.themeColor) {
    case 'primary':
      iconClass = 'cwx-icon cwx-icon-before_information-default';
      break;
    case 'secondary':
      iconClass = 'cwx-icon cwx-icon-before_check-outline';
      break;
    case 'tertiary':
      iconClass = 'cwx-icon cwx-icon-before_check-outline';
      break;
    case 'info':
      iconClass = 'cwx-icon cwx-icon-before_information-default';
      break;
    case 'success':
      iconClass = 'cwx-icon cwx-icon-before_check-outline';
      break;
    case 'error':
      iconClass = 'cwx-icon cwx-icon-before_close-outline';
      break;
    case 'warning':
      iconClass = 'cwx-icon cwx-icon-before_warning-active';
      break;
    default:
      iconClass = 'cwx-icon cwx-icon-before_information-default';
      break;
  }
  return props.iconClass ?? iconClass;
});

// WCAG 2.2 AA compliant ARIA attributes
const ariaRole = computed(() => {
  // Use 'alert' for critical/time-sensitive notifications (errors, warnings)
  // Use 'status' for informational notifications (info, success, etc.)
  return props.themeColor === 'error' || props.themeColor === 'warning' ? 'alert' : 'status';
});

const ariaLive = computed(() => {
  // 'assertive' interrupts current activity for critical messages
  // 'polite' waits for natural pause for informational messages
  return props.themeColor === 'error' || props.themeColor === 'warning' ? 'assertive' : 'polite';
});

// Generate unique ID for title element to enable aria-labelledby
// Using ref to ensure stable ID per component instance
const titleIdRef = ref<string | undefined>(undefined);

// Watch for title changes and generate ID when title is present
watch(
  () => props.title,
  (newTitle) => {
    if (newTitle && !titleIdRef.value) {
      titleIdRef.value = `notification-title-${Math.random().toString(36).substr(2, 9)}`;
    }
  },
  { immediate: true },
);

const titleId = computed(() => {
  return props.title ? titleIdRef.value : undefined;
});

const ariaLabel = computed(() => {
  // Provide accessible label combining notification type and title if available
  const typeLabel = t('Common.Notification');
  return props.title ? `${typeLabel}: ${props.title}` : typeLabel;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["notification", [dynamicClass.value, { 'notification--subtle': __props.type === 'subtle' }]]),
    role: ariaRole.value,
    "aria-live": ariaLive.value,
    "aria-atomic": "true",
    "aria-label": __props.title ? undefined : ariaLabel.value,
    "aria-labelledby": __props.title ? titleId.value : undefined
  }, [
    _createElementVNode("div", _hoisted_2, [
      (__props.withIcon)
        ? (_openBlock(), _createElementBlock("i", {
            key: 0,
            class: _normalizeClass(["notification__icon", notificationIconClass.value]),
            "aria-hidden": "true"
          }, null, 2))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        (__props.title)
          ? (_openBlock(), _createElementBlock("span", {
              key: 0,
              id: titleId.value,
              class: "notification__title"
            }, _toDisplayString(__props.title), 9, _hoisted_4))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_5, [
          _renderSlot(_ctx.$slots, "default")
        ])
      ])
    ])
  ], 10, _hoisted_1))
}
}

})